// src/pages/Message.js
import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { Typography, Container, Box, Paper, Button, Alert } from '@mui/material';
import HomeIcon from '@mui/icons-material/Home';
import VolumeUpIcon from '@mui/icons-material/VolumeUp';

// Names that are recognized and should be told to go away
const recognizedNames = ['Ruby', 'Ari', 'Elize', 'Alex', 'David'];

// Names that have audio files (subset of recognized names)
const namesWithAudio = ['Ruby', 'Ari', 'Elize', 'Alex'];

const Message = () => {
  const { name: pathName } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const audioRef = useRef(null);
  const [audioError, setAudioError] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);

  // Get name from either path parameter or query parameter
  const name = pathName || searchParams.get('name');

  // Normalize the name (capitalize first letter, lowercase the rest)
  const normalizedName = name ? name.charAt(0).toUpperCase() + name.slice(1).toLowerCase() : '';
  
  const isRecognized = recognizedNames.includes(normalizedName);
  const hasAudio = namesWithAudio.includes(normalizedName);

  useEffect(() => {
    // Try to auto-play audio if the name has an audio file, but handle gracefully if blocked
    if (hasAudio && audioRef.current) {
      const playAudio = async () => {
        try {
          setIsPlaying(true);
          await audioRef.current.play();
        } catch (error) {
          console.log('Auto-play blocked by browser (expected behavior):', error.message);
          // Auto-play was blocked, which is normal - just reset the playing state
          setIsPlaying(false);
          // Don't set audioError for auto-play failures, as this is expected behavior
        }
      };
      
      // Small delay to ensure page is loaded
      const timer = setTimeout(playAudio, 500);
      return () => clearTimeout(timer);
    }
  }, [hasAudio, normalizedName]);

  const handleAudioEnded = () => {
    setIsPlaying(false);
  };

  const handleAudioError = () => {
    setAudioError(true);
    setIsPlaying(false);
  };

  const playAudioManually = async () => {
    if (audioRef.current) {
      try {
        setIsPlaying(true);
        setAudioError(false);
        await audioRef.current.play();
      } catch (error) {
        console.error('Error playing audio:', error);
        setAudioError(true);
        setIsPlaying(false);
      }
    }
  };

  const renderContent = () => {
    if (!name) {
      return (
        <Alert severity="info">
          No name provided. Please add a name to the URL or go back to the home page.
        </Alert>
      );
    }

    if (!isRecognized) {
      // Case 3: Name is not recognized
      return (
        <Paper elevation={3} sx={{ p: 4, textAlign: 'center', backgroundColor: '#e8f5e8' }}>
          <Typography variant="h2" component="h1" gutterBottom color="success.main">
            🎉 You can stay! 🎉
          </Typography>
          <Typography variant="h5" color="text.secondary">
            {normalizedName} is welcome here!
          </Typography>
        </Paper>
      );
    }

    // Case 1 & 2: Name is recognized
    return (
      <Paper elevation={3} sx={{ p: 4, textAlign: 'center', backgroundColor: '#ffeee8' }}>
        <Typography variant="h2" component="h1" gutterBottom color="error.main">
          Go Away {normalizedName}! 👋
        </Typography>
        
        {hasAudio && (
          <Box mt={3}>
            <audio
              ref={audioRef}
              onEnded={handleAudioEnded}
              onError={handleAudioError}
              preload="auto"
            >
              <source src={`/audio/${normalizedName.toLowerCase()}.m4a`} type="audio/mp4" />
              <source src={`/audio/${normalizedName.toLowerCase()}.mp3`} type="audio/mpeg" />
              <source src={`/audio/${normalizedName.toLowerCase()}.wav`} type="audio/wav" />
              Your browser does not support the audio element.
            </audio>
            
            {isPlaying && (
              <Alert severity="info" sx={{ mb: 2 }}>
                🔊 Playing audio message...
              </Alert>
            )}
            
            {!isPlaying && hasAudio && (
              <Alert severity="info" sx={{ mb: 2 }}>
                🎵 Click below to hear the personalized message!
              </Alert>
            )}
            
            {audioError && (
              <Alert severity="warning" sx={{ mb: 2 }}>
                Audio file could not be loaded. Please try again.
              </Alert>
            )}
            
            <Button
              variant="contained"
              size="large"
              startIcon={<VolumeUpIcon />}
              onClick={playAudioManually}
              disabled={isPlaying}
              sx={{ 
                mt: 1,
                backgroundColor: '#ff5722',
                '&:hover': {
                  backgroundColor: '#e64a19'
                }
              }}
            >
              {isPlaying ? 'Playing...' : '🔊 Play Audio Message'}
            </Button>
          </Box>
        )}
        
        {!hasAudio && (
          <Typography variant="h6" color="text.secondary" sx={{ mt: 2 }}>
            (Text message only)
          </Typography>
        )}
      </Paper>
    );
  };

  return (
    <Container maxWidth="md">
      <Box 
        display="flex" 
        flexDirection="column" 
        alignItems="center" 
        justifyContent="center" 
        minHeight="100vh"
        sx={{ py: 4 }}
      >
        {renderContent()}
        
        <Button
          variant="contained"
          startIcon={<HomeIcon />}
          onClick={() => navigate('/')}
          sx={{ mt: 4 }}
        >
          Back to Home
        </Button>
      </Box>
    </Container>
  );
};

export default Message;